import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, ShoppingBag, PackagePlus, RotateCcw, Sparkles, RefreshCw, ClipboardList } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { api, formatDateTime } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const KIND = {
  create: { label: "Opprettet", Icon: Sparkles, tone: "text-[hsl(var(--info))] bg-[hsl(var(--info))]/10" },
  adjust: { label: "Justert", Icon: PackagePlus, tone: "text-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]" },
  sale: { label: "Solgt", Icon: ShoppingBag, tone: "text-foreground bg-secondary" },
  restore: { label: "Tilbake på lager", Icon: RotateCcw, tone: "text-[hsl(var(--success))] bg-[hsl(var(--success))]/10" },
  count: { label: "Lagertelling", Icon: ClipboardList, tone: "text-[hsl(var(--warning-foreground))] bg-[hsl(var(--warning))]/15" },
};

/**
 * Lagerhistorikk for éi vare: sal (med ordrenummer), påfyll/justering (kven), tilbakeføring ved kansellering/refusjon.
 * `refreshKey` kan endrast (t.d. etter lagring) for å laste på nytt.
 */
export const StockLog = ({ productId, refreshKey, className = "" }) => {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api
      .get(`/my/shop/products/${productId}/stock-log`, { params: { limit: 100 } })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, [productId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const entries = data?.entries || [];
  const totals = data?.totals || {};
  const actorLabel = (a) => (a === "system" ? t("Automatisk") : a === "kjøpar" ? t("Kjøper") : a || "—");

  return (
    <Card className={`border-border ${className}`} data-testid="stock-log-card">
      <CardHeader className="pb-3 flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><History className="w-5 h-5 text-muted-foreground" /> {t("Lagerhistorikk")}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">{t("Salg, påfyll og hvem som endret lageret – nyeste øverst.")}</p>
        </div>
        {!loading && !error && (
          <div className="flex gap-3 text-xs text-muted-foreground tabular-nums shrink-0" data-testid="stock-log-totals">
            <span>{t("Solgt")}: <strong className="text-foreground">{totals.sold || 0}</strong></span>
            <span>{t("Tilbake")}: <strong className="text-foreground">{totals.restored || 0}</strong></span>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4 space-y-2" data-testid="stock-log-loading">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
        ) : error ? (
          <div className="p-6 text-center space-y-2" data-testid="stock-log-error">
            <p className="text-sm text-muted-foreground">{t("Kunne ikke laste lagerhistorikken")}</p>
            <Button variant="outline" size="sm" onClick={load} data-testid="stock-log-retry"><RefreshCw className="w-4 h-4 mr-1.5" /> {t("Prøv igjen")}</Button>
          </div>
        ) : entries.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center" data-testid="stock-log-empty">{t("Ingen lagerendringer ennå.")}</p>
        ) : (
          <ol className="divide-y divide-border" data-testid="stock-log-list">
            {entries.map((e) => {
              const k = KIND[e.kind] || KIND.adjust;
              const positive = e.delta > 0;
              return (
                <li key={e.id} className="flex items-center gap-3 px-4 py-3" data-testid={`stock-log-row-${e.id}`}>
                  <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${k.tone}`}><k.Icon className="w-4 h-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-snug">
                      {t(k.label)}
                      {e.order_number && (
                        <>
                          {" · "}
                          <Link to={`/dashboard/butikk/ordrer?q=${encodeURIComponent(e.order_number)}`} className="font-mono text-xs underline underline-offset-4 hover:text-foreground text-muted-foreground" data-testid={`stock-log-order-link-${e.id}`}>{e.order_number}</Link>
                        </>
                      )}
                      {e.note && <span className="text-muted-foreground font-normal"> · {e.note}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(e.created_at)} · {actorLabel(e.actor)}</p>
                  </div>
                  <div className="text-right shrink-0 tabular-nums">
                    <p className={`text-sm font-semibold ${positive ? "text-[hsl(var(--success))]" : e.delta < 0 ? "text-foreground" : "text-muted-foreground"}`} data-testid={`stock-log-delta-${e.id}`}>{positive ? `+${e.delta}` : e.delta}</p>
                    <p className="text-xs text-muted-foreground">{t("Lager")}: {e.stock_after}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};
